// @system — Global date-range context.
//
// Holds a single "active reporting window" shared across the app (dashboard
// widgets, admin financial reports, …). A page's range selects read from and
// write to this context so navigating between reports keeps the window stable.
//
// Dates are stored as plain `YYYY-MM-DD` strings (UTC day granularity). That
// keeps <input type="date"> binding and the `startDate`/`endDate` query params
// (also `YYYY-MM-DD`) trivial and deterministic in every timezone.
//
// Value shape returned by useGlobalDateRange():
//   {
//     rangeStart: "YYYY-MM-DD",
//     rangeEnd:   "YYYY-MM-DD",
//     preset:     "last7" | "last30" | "custom",
//     applyRange(next),   // legacy alias for setRange
//     setRange({ rangeStart, rangeEnd, preset }) => void,
//   }
import { createContext, useCallback, useContext, useMemo, useState } from "react"

/** Named windows offered by range pickers; `custom` = hand-picked anchors. */
/** @typedef {'last7' | 'last30' | 'last90' | 'custom' | string} RangePreset */

/**
 * @typedef {Object} GlobalDateRange
 * @property {string} rangeStart `YYYY-MM-DD`
 * @property {string} rangeEnd `YYYY-MM-DD`
 * @property {RangePreset} preset
 */

/** Partial update accepted by `setRange`; invalid anchors fall back to the initial window. */
/** @typedef {{ rangeStart?: string | null, rangeEnd?: string | null, preset?: RangePreset | null }} GlobalDateRangeInput */

/**
 * @typedef {GlobalDateRange & {
 *   setRange: (next: GlobalDateRangeInput) => void,
 *   applyRange: (next: GlobalDateRangeInput) => void,
 * }} GlobalDateRangeContextValue
 */

export const GLOBAL_RANGE_STORAGE_KEY = "system.globalDateRange"
/** @type {Record<string, { label: string, days: number }>} */
export const GLOBAL_RANGE_PRESETS = {
  last7: { label: "Last 7 days", days: 7 },
  last30: { label: "Last 30 days", days: 30 },
  last90: { label: "Last 90 days", days: 90 },
}

// UTC day arithmetic helpers (kept plain for easy unit testing).
const DAY_MS = 24 * 60 * 60 * 1000

/**
 * @param {Date | string | number} [date]
 * @returns {string | null} `YYYY-MM-DD`, or null for an invalid date
 */
export function toDateOnlyISO(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString().slice(0, 10)
}

/**
 * @param {string} iso `YYYY-MM-DD`
 * @param {number} days
 * @returns {string} Shifted `YYYY-MM-DD`; `iso` itself when unparsable
 */
export function addDaysISO(iso, days) {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00.000Z`)
  if (Number.isNaN(d.getTime())) return iso
  d.setTime(d.getTime() + days * DAY_MS)
  return d.toISOString().slice(0, 10)
}

/**
 * @param {string} iso `YYYY-MM-DD`
 * @param {number} days
 */
export function subDaysISO(iso, days) {
  return addDaysISO(iso, -days)
}

/** @returns {string} Today as `YYYY-MM-DD` (UTC) */
export function todayISO() {
  // `new Date()` is always valid, so the null branch of toDateOnlyISO cannot happen.
  return /** @type {string} */ (toDateOnlyISO(new Date()))
}

/**
 * @param {unknown} value
 * @returns {value is string}
 */
export function isISOAnchor(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
}

/**
 * Normalise a 30-day default window: today back through today - 29.
 * @param {Date | string | number} [now]
 * @returns {GlobalDateRange}
 */
export function defaultGlobalRange(now = new Date()) {
  // An unparsable `now` used to throw inside subDaysISO; anchor on today instead.
  const end = toDateOnlyISO(now) ?? todayISO()
  return {
    rangeStart: subDaysISO(end, GLOBAL_RANGE_PRESETS.last30.days - 1),
    rangeEnd: end,
    preset: "last30",
  }
}

/** @returns {GlobalDateRange} */
function storedInitialRange() {
  if (typeof localStorage === "undefined") return defaultGlobalRange()
  try {
    const raw = localStorage.getItem(GLOBAL_RANGE_STORAGE_KEY)
    if (!raw) return defaultGlobalRange()
    /** @type {unknown} */
    const parsed = JSON.parse(raw)
    if (typeof parsed === "object" && parsed !== null) {
      const { rangeStart, rangeEnd, preset } = /** @type {Record<string, unknown>} */ (parsed)
      if (isISOAnchor(rangeStart) && isISOAnchor(rangeEnd)) {
        const now = todayISO()
        const end = rangeEnd > now ? now : rangeEnd
        const start = rangeStart > end ? end : rangeStart
        return {
          rangeStart: start,
          rangeEnd: end,
          preset: preset === "custom" ? "custom" : "last30",
        }
      }
    }
  } catch {
    /* corrupted storage — fall through to defaults */
  }
  return defaultGlobalRange()
}

const GlobalDateRangeContext = createContext(/** @type {GlobalDateRangeContextValue | null} */ (null))

/** @param {WithChildren & { storageKey?: string }} props */
export function GlobalDateRangeProvider({ children, storageKey = GLOBAL_RANGE_STORAGE_KEY }) {
  const [initial] = useState(() => storedInitialRange())
  const [range, setRangeState] = useState(initial)

  const persist = useCallback(
    /** @param {GlobalDateRange} next */
    (next) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(next))
      } catch {
        /* persistence is best-effort (private mode etc.) */
      }
    }, [storageKey])

  const setRange = useCallback(
    /** @param {GlobalDateRangeInput} next */
    ({ rangeStart, rangeEnd, preset }) => {
      // Swap when reversed so rangeStart is always <= rangeEnd.
      let start = isISOAnchor(rangeStart) ? rangeStart : initial.rangeStart
      let end = isISOAnchor(rangeEnd) ? rangeEnd : initial.rangeEnd
      if (start > end) [start, end] = [end, start]
      /** @type {GlobalDateRange} */
      const next = { rangeStart: start, rangeEnd: end, preset: preset || "custom" }
      setRangeState(next)
      persist(next)
    }, [initial.rangeStart, initial.rangeEnd, persist])

  /** @type {GlobalDateRangeContextValue} */
  const value = useMemo(() => ({
    ...range,
    setRange,
    applyRange: setRange,
  }), [range, setRange])

  return (
    <GlobalDateRangeContext.Provider value={value}>
      {children}
    </GlobalDateRangeContext.Provider>
  )
}

/** @returns {GlobalDateRangeContextValue} */
export function useGlobalDateRange() {
  const ctx = useContext(GlobalDateRangeContext)
  if (!ctx) throw new Error("useGlobalDateRange must be used inside <GlobalDateRangeProvider>")
  return ctx
}

export default GlobalDateRangeProvider
